/**
 * Scorecard Visibility Table Configuration
 * Column definitions for the scorecard tables
 */

import { useMemo, useState, useEffect, type ElementType } from 'react';
import { Avatar, Center, Flex, Icon, Tooltip } from '@chakra-ui/react';
import { KText, KTag } from '@/ui-library/components';
import { HeaderCell } from '@/components/ats/Table/tableCells';
import { InfoCircle } from '@kula-ai/icons/dual-tone/InfoCircle';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { ratingOptionsConfig } from '@/constants/scorecard-configs';
import { Eye } from '@kula-ai/icons/outline/Eye';
import { Calendar } from '@kula-ai/icons/outline/Calendar';
import { LuClipboardCheck } from 'react-icons/lu';
import { ThumbsUpSmall } from '@/assets/icons/ats';
import { TransferredBadge } from '@/components/candidate-job-slider-v1/components/common/TransferredBadge';
import {
  type ScorecardItem,
  type ScorecardVisibilityMode,
} from '@/components/shared/interviews-revamp/scorecard/scorecard-visibility/types';
import {
  isOwnScorecard,
  getRecordTypeLabel,
} from '@/components/shared/interviews-revamp/scorecard/scorecard-visibility/helpers';
import { CurrentInterviewActions } from '@/components/shared/interviews-revamp/scorecard/scorecard-visibility/components/CurrentInterviewActions';
import { HistoryActions } from '@/components/shared/interviews-revamp/scorecard/scorecard-visibility/components/HistoryActions';
import { MeasuredActionsCell } from '@/components/shared/interviews-revamp/scorecard/scorecard-visibility/components/MeasuredActionsCell';
import dayjs from 'dayjs';
import { useDateFormat } from '@/hooks';
import { type InterviewType } from '@/types/interview';
import type { ApplicationData } from '@/components/candidate-job-slider-v1/types/application.interface';

const iconItems = {
  AtsInterview: Calendar,
  AtsReview: Eye,
  AtsAssessment: LuClipboardCheck,
};

interface TableConfigParams {
  scorecards: ScorecardItem[];
  interview: InterviewType;
  currentUserId: number;
  mode: ScorecardVisibilityMode;
  hasCurrentUserSubmitted: boolean;
  onSubmitScorecard: (_: ScorecardItem) => void;
  onViewScorecard: (_: ScorecardItem) => void;
  onEditScorecard: (_: ScorecardItem) => void;
  onSendReminder: (_: ScorecardItem) => void;
  refetch?: () => void;
  transferInfo?: {
    history?: number[];
    applications?: Record<string, unknown>;
  } | null;
}

/**
 * Reviewer cell - shows avatar, name, and "You" badge if current user
 */
const getReviewerCellWidth = (
  scorecards: ScorecardItem[],
  currentUserId: number
) => {
  return scorecards.some(
    (sc) => sc.edited_at !== null || isOwnScorecard(sc, currentUserId)
  )
    ? 250
    : 200;
};
const createReviewerCell = (
  currentUserId: number,
  scorecards: ScorecardItem[],
  transferInfo?: {
    history?: number[];
    applications?: Record<string, unknown>;
  } | null
) => ({
  Header: () => (
    <HeaderCell
      title="Interviewer / Reviewer"
      titleProps={{ whiteSpace: 'nowrap' }}
    />
  ),
  accessor: 'reviewer',
  style: {
    borderRight: 'none',
    width: getReviewerCellWidth(scorecards, currentUserId),
    maxWidth: getReviewerCellWidth(scorecards, currentUserId),
  },
  headerStyle: {
    width: getReviewerCellWidth(scorecards, currentUserId),
    maxWidth: getReviewerCellWidth(scorecards, currentUserId),
  },
  Cell: ({ row }) => {
    const scorecard = row.original as ScorecardItem;
    const isOwn = isOwnScorecard(scorecard, currentUserId);
    const { dateFormat, timeFormat } = useDateFormat();

    return (
      <Flex alignItems="center" w="100%" gap="8px">
        <Avatar
          className="avatar-sm"
          src={scorecard.reviewer?.avatar || undefined}
          width="28px"
          height="28px"
          borderRadius="8px"
          name={scorecard.reviewer?.name}
          flexShrink={0}
        />
        <TruncatedTextWithTooltip
          maxW="100%"
          textTransform="capitalize"
          text={scorecard.reviewer?.name}
          variant="heading_3"
        />
        {scorecard.is_transferred && transferInfo && (
          <TransferredBadge
            transferInfo={
              transferInfo as NonNullable<ApplicationData['transfer_info']>
            }
            sourceApplicationId={scorecard.ats_application_id}
          />
        )}
        {isOwn && (
          <KTag
            value="You"
            bg="primary-50"
            color="primary-600"
            borderRadius="4px"
            px="6px"
            py="2px"
            fontSize="xs"
            flexShrink={0}
          />
        )}
        {scorecard.edited_at &&
          scorecard.edited_by?.id &&
          scorecard.status === 'submitted' && (
            <KTag
              _groupHover={{ bg: 'base-white', borderColor: 'border-color' }}
              transition="background 0.15s ease"
              value={
                <Flex alignItems="center" gap="6px">
                  <KText
                    variant="paragraph"
                    fontWeight="550"
                    whiteSpace="nowrap"
                  >
                    Edited
                  </KText>
                  <Tooltip
                    label={
                      <Flex flexDir="column">
                        <KText variant="paragraph" color="base-white">
                          {dayjs(scorecard.edited_at).format(
                            `${dateFormat?.label}, ${timeFormat}`
                          )}
                        </KText>
                        <KText variant="paragraph" color="base-white">
                          Edited by {scorecard.edited_by?.name}
                        </KText>
                        {scorecard.previous_rating && (
                          <KText variant="paragraph" color="base-white">
                            Rating: From{' '}
                            {
                              ratingOptionsConfig.find(
                                (item) =>
                                  item?.value === scorecard.previous_rating.from
                              )?.label
                            }{' '}
                            to{' '}
                            {
                              ratingOptionsConfig.find(
                                (item) =>
                                  item?.value === scorecard.previous_rating.to
                              )?.label
                            }
                          </KText>
                        )}
                      </Flex>
                    }
                    hasArrow
                    shouldWrapChildren
                  >
                    <Center>
                      <Icon as={InfoCircle as ElementType} />
                    </Center>
                  </Tooltip>
                </Flex>
              }
            />
          )}
      </Flex>
    );
  },
});

/**
 * Rating cell - shows rating badge or "Pending"
 */
const createRatingCell = (onViewScorecard: (_: ScorecardItem) => void) => ({
  Header: () => <HeaderCell title="Rating" />,
  accessor: 'rating',
  style: {
    maxWidth: 140,
    width: 140,
  },
  headerStyle: {
    maxWidth: 140,
    width: 140,
  },
  Cell: ({ row }) => {
    const scorecard = row.original as ScorecardItem;
    const { rating, status } = scorecard;

    // Show pending if not submitted
    if (status !== 'submitted' || !rating) {
      return (
        <KTag
          value="Pending"
          color="pastel-6-dark"
          bg="pastel-6-light"
          borderRadius="4px"
        />
      );
    }

    const ratingConfig = ratingOptionsConfig.find(
      (option) => option.value === rating
    );

    if (!ratingConfig) return null;

    return (
      <Flex
        gap="6px"
        alignItems="center"
        borderRadius="4px"
        bg={ratingConfig.bgColor}
        p="2px 8px"
        cursor="pointer"
        onClick={() => onViewScorecard(scorecard)}
        className={rating === 'strong_yes' ? 'checked_strong_yes' : ''}
      >
        <Icon
          as={
            (rating === 'strong_yes'
              ? ThumbsUpSmall
              : ratingConfig.icon) as ElementType
          }
          w="16px"
          h="16px"
          color={ratingConfig.selectedColor}
        />
        <KText size="sm-medium" color={ratingConfig.selectedColor}>
          {ratingConfig.label}
        </KText>
      </Flex>
    );
  },
});

/**
 * Submission time cell - shows relative time
 */
// const submissionCell = {
//   Header: () => <HeaderCell title="Submission" />,
//   minWidth: 100,
//   width: 120,
//   accessor: 'submitted_at',
//   Cell: ({ row }) => {
//     const scorecard = row.original as ScorecardItem;
//     const formattedTime = formatSubmissionTime(scorecard.submitted_at);

//     return (
//       <KText size="sm" color="secondary-700" whiteSpace="nowrap">
//         {formattedTime}
//       </KText>
//     );
//   },
// };

/**
 * Activity cell - shows record type with icon
 */
const activityCell = {
  Header: () => <HeaderCell title="Activity" />,
  accessor: 'record_type',
  Cell: ({ row }) => {
    const scorecard = row.original as ScorecardItem;
    const IconComponent = iconItems[scorecard.record_type] || Calendar;

    return (
      <Flex
        alignItems="center"
        border="1px solid"
        borderColor="border-color"
        borderRadius="4px"
        p="2px 8px"
        gap="6px"
        // maxW="150px"
        backgroundColor="white"
      >
        <Icon
          as={IconComponent as ElementType}
          width="14px"
          height="14px"
          color="ats-primary"
          flexShrink={0}
        />
        <TruncatedTextWithTooltip
          text={`${getRecordTypeLabel(scorecard.record_type)} - ${
            scorecard.record?.name || ''
          }`}
          maxW="100px"
          size="sm"
        />
      </Flex>
    );
  },
};

/**
 * Stage cell - shows stage name
 */
// const stageCell = {
//   Header: () => <HeaderCell title="Stage" />,
//   minWidth: 100,
//   width: 120,
//   accessor: 'stage',
//   Cell: ({ row }) => {
//     const scorecard = row.original as ScorecardItem;

//     return (
//       <TruncatedTextWithTooltip
//         text={scorecard.stage?.name || '-'}
//         maxW="100px"
//         border="1px solid"
//         borderColor="border-color"
//         borderRadius="4px"
//         p="2px 8px"
//         size="sm"
//       />
//     );
//   },
// };

/** Initial width when measuring; column grows to max content width across rows */
const ACTIONS_COLUMN_MIN_WIDTH = 80;
/** Fixed width when not using measurement (e.g. getCurrentInterviewTableConfig) */
const ACTIONS_COLUMN_DEFAULT_WIDTH = 240;
/** History actions column default width when not measuring */
const HISTORY_ACTIONS_COLUMN_DEFAULT_WIDTH = 120;
/** Table cell horizontal padding (6px 12px) so content doesn't overflow */
const ACTIONS_CELL_PADDING_BUFFER = 24;

/**
 * Create current interview action cell.
 * When measureOptions is provided, column width is set from the largest content width across rows.
 */
const createCurrentInterviewActionCell = (
  params: TableConfigParams,
  measureOptions?: {
    onMeasureWidth: (_width: number) => void;
    actionsColumnWidth: number;
  }
) => {
  const width = measureOptions
    ? measureOptions.actionsColumnWidth
    : ACTIONS_COLUMN_DEFAULT_WIDTH;
  return {
    Header: () => <HeaderCell title="" />,
    minWidth: width,
    width: width,
    accessor: 'id',
    style: {
      position: 'sticky' as const,
      right: 0,
      zIndex: 1,
      width,
      minWidth: width,
      maxWidth: width,
      background: 'white',
      justifyContent: 'flex-end',
      borderLeft: '1px solid var(--kula-colors-border-color)',
      boxShadow: '-4px 0 8px rgba(0, 0, 0, 0.06)',
    },
    Cell: ({ row }: { row: { original: ScorecardItem } }) => {
      const scorecard = row.original;
      const content = (
        <Flex bg="inherit" w="100%">
          <CurrentInterviewActions
            interview={params.interview}
            scorecard={scorecard}
            currentUserId={params.currentUserId}
            mode={params.mode}
            hasCurrentUserSubmitted={params.hasCurrentUserSubmitted}
            onSubmitScorecard={params.onSubmitScorecard}
            onViewScorecard={params.onViewScorecard}
            onEditScorecard={params.onEditScorecard}
            onSendReminder={params.onSendReminder}
            refetch={params.refetch}
          />
        </Flex>
      );

      if (measureOptions) {
        return (
          <MeasuredActionsCell onMeasure={measureOptions.onMeasureWidth}>
            <Flex bg="inherit" w="max-content" justifyContent="flex-end">
              <CurrentInterviewActions
                interview={params.interview}
                scorecard={scorecard}
                currentUserId={params.currentUserId}
                mode={params.mode}
                hasCurrentUserSubmitted={params.hasCurrentUserSubmitted}
                onSubmitScorecard={params.onSubmitScorecard}
                onViewScorecard={params.onViewScorecard}
                onEditScorecard={params.onEditScorecard}
                onSendReminder={params.onSendReminder}
                refetch={params.refetch}
              />
            </Flex>
          </MeasuredActionsCell>
        );
      }
      return content;
    },
  };
};

/**
 * Create history action cell.
 * When measureOptions is provided, column width is set from the largest content width across rows.
 */
const createHistoryActionCell = (
  onViewScorecard: (_sc: ScorecardItem) => void,
  measureOptions?: {
    onMeasureWidth: (_w: number) => void;
    actionsColumnWidth: number;
  }
) => {
  const width = measureOptions
    ? measureOptions.actionsColumnWidth
    : HISTORY_ACTIONS_COLUMN_DEFAULT_WIDTH;
  return {
    Header: () => <HeaderCell title="" />,
    minWidth: width,
    width: width,
    accessor: 'id',
    style: {
      position: 'sticky' as const,
      right: 0,
      zIndex: 1,
      width,
      minWidth: width,
      maxWidth: width,
      background: 'white',
      padding: '6px 4px',
      justifyContent: 'flex-end',
      borderLeft: '1px solid var(--kula-colors-border-color)',
      boxShadow: '-4px 0 8px rgba(0, 0, 0, 0.06)',
    },
    Cell: ({ row }: { row: { original: ScorecardItem } }) => {
      const scorecard = row.original;
      const content = (
        <Flex bg="inherit" justifyContent="flex-end">
          <HistoryActions
            scorecard={scorecard}
            onViewScorecard={onViewScorecard}
          />
        </Flex>
      );

      if (measureOptions) {
        return (
          <MeasuredActionsCell onMeasure={measureOptions.onMeasureWidth}>
            <Flex bg="inherit" w="max-content" justifyContent="flex-end">
              <HistoryActions
                scorecard={scorecard}
                onViewScorecard={onViewScorecard}
              />
            </Flex>
          </MeasuredActionsCell>
        );
      }
      return content;
    },
  };
};

/**
 * Get table configuration for current interview section (static width for actions column).
 */
export const getCurrentInterviewTableConfig = (params: TableConfigParams) => {
  const { scorecards, interview } = params;
  const isRatingPending = scorecards?.some((sc) => sc.rating === null);
  const showRatingCell = interview?.can_edit ? true : !isRatingPending;

  return [
    createReviewerCell(params.currentUserId, scorecards, params.transferInfo),
    ...(showRatingCell ? [createRatingCell(params.onViewScorecard)] : []),
    activityCell,
    createCurrentInterviewActionCell(params),
  ];
};

/**
 * Hook that returns table config with actions column width set from the largest content width across rows.
 */
export const useCurrentInterviewTableConfig = (params: TableConfigParams) => {
  const { scorecards, interview } = params;
  const [actionsColumnWidth, setActionsColumnWidth] = useState(
    ACTIONS_COLUMN_MIN_WIDTH
  );

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setActionsColumnWidth(ACTIONS_COLUMN_MIN_WIDTH);
    });
    return () => cancelAnimationFrame(id);
  }, [scorecards?.length]);

  const onMeasureWidth = useMemo(
    () => (w: number) =>
      setActionsColumnWidth((prev) =>
        Math.max(prev, Math.ceil(w) + ACTIONS_CELL_PADDING_BUFFER)
      ),
    []
  );

  return useMemo(() => {
    const isRatingPending = scorecards?.some((sc) => sc.rating === null);
    const showRatingCell = interview?.can_edit ? true : !isRatingPending;

    return [
      createReviewerCell(params.currentUserId, scorecards, params.transferInfo),
      ...(showRatingCell ? [createRatingCell(params.onViewScorecard)] : []),
      activityCell,
      createCurrentInterviewActionCell(params, {
        onMeasureWidth,
        actionsColumnWidth,
      }),
    ];
  }, [
    params,
    scorecards,
    interview?.can_edit,
    actionsColumnWidth,
    onMeasureWidth,
  ]);
};

/**
 * Get table configuration for history section (static width for actions column).
 */
export const getHistoryTableConfig = (
  onViewScorecard: (_: ScorecardItem) => void,
  scorecards: ScorecardItem[],
  interview: InterviewType,
  transferInfo?: {
    history?: number[];
    applications?: Record<string, unknown>;
  } | null
) => {
  const isRatingPending = scorecards?.some((sc) => sc.rating === null);
  const showRatingCell = interview?.can_edit ? true : !isRatingPending;

  return [
    createReviewerCell(0, scorecards, transferInfo),
    ...(showRatingCell ? [createRatingCell(onViewScorecard)] : []),
    activityCell,
    createHistoryActionCell(onViewScorecard),
  ];
};

/**
 * Hook that returns history table config with actions column width set from the largest content width across rows.
 */
export const useHistoryTableConfig = (
  onViewScorecard: (_: ScorecardItem) => void,
  scorecards: ScorecardItem[],
  interview: InterviewType,
  transferInfo?: {
    history?: number[];
    applications?: Record<string, unknown>;
  } | null
) => {
  const [actionsColumnWidth, setActionsColumnWidth] = useState(
    ACTIONS_COLUMN_MIN_WIDTH
  );

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setActionsColumnWidth(ACTIONS_COLUMN_MIN_WIDTH);
    });
    return () => cancelAnimationFrame(id);
  }, [scorecards?.length]);

  const onMeasureWidth = useMemo(
    () => (w: number) =>
      setActionsColumnWidth((prev) =>
        Math.max(prev, Math.ceil(w) + ACTIONS_CELL_PADDING_BUFFER)
      ),
    []
  );

  return useMemo(() => {
    const isRatingPending = scorecards?.some((sc) => sc.rating === null);
    const showRatingCell = interview?.can_edit ? true : !isRatingPending;

    return [
      createReviewerCell(0, scorecards, transferInfo),
      ...(showRatingCell ? [createRatingCell(onViewScorecard)] : []),
      activityCell,
      createHistoryActionCell(onViewScorecard, {
        onMeasureWidth,
        actionsColumnWidth,
      }),
    ];
  }, [
    onViewScorecard,
    scorecards,
    interview?.can_edit,
    actionsColumnWidth,
    onMeasureWidth,
    transferInfo,
  ]);
};
